/*
 * decaffeinate suggestions:
 * DS101: Remove unnecessary use of Array.from
 * DS102: Remove unnecessary code created because of implicit returns
 * DS104: Avoid inline assignments
 * DS201: Simplify complex destructure assignments
 * DS202: Simplify dynamic range loops
 * DS204: Change includes calls to have a more natural evaluation order
 * DS205: Consider reworking code to avoid use of IIFEs
 * DS207: Consider shorter variations of null checks
 * Full docs: https://github.com/decaffeinate/decaffeinate/blob/master/docs/suggestions.md
 */
import {clamp, chunk} from '@benchling/js-utils';
import _ from 'underscore';

import {assert} from 'Asserts';
import * as benchlingUtil from 'BenchlingUtil';
import {
  AMINO_ACID_TO_DEGENERATE_DNA_CODONS,
  DEGENERATE_DNA_CODE,
  DEFAULT_DNA_START_CODON,
  StandardDNACodon,
  GeneticCodeData,
} from 'biojs/Codons';
import {NON_STANDARD_AMINO_ACIDS} from 'biojs/Codons';
import {PartAttributes} from 'biojs/Part';
import {OPPOSITE_STRAND, Strand} from 'constants/MolbioConstants';
import {PrimerPreferences} from 'constants/PrimerConstants';
import {PolymerFeatureAttributes, PolymerFeatureModel} from 'constants/SequenceConstants';
import geneticCodesDNA from 'data/geneticCodesDNA.json';
import geneticCodesRNA from 'data/geneticCodesRNA.json';
import areAminoAcidsValid from 'molbio/aa-sequences/util/areAminoAcidsValid';
import {DigestableEnzyme, Enzyme} from 'molbio/enzymes/EnzymeConstants';
import * as sequtil from 'molbio/SeqUtil';
import preferences from 'Preferences';
import {matchCase} from 'util/CapitalizationUtil';
import ErrorSubclass from 'util/ErrorSubclass';
import lruMemoize from 'util/lruMemoize';

import {
  CodonTranslationTable,
  DEGENERATE_BASES,
  DNA_COMPLEMENT_TABLE,
  DNA_IUPAC_TO_BASES,
  EnergyDeltas,
  GAP,
  GAP_PATTERN,
  IUPAC,
  IUPAC_TO_REGEX,
  IUPAC_TO_RNA_AGNOSTIC_REGEX,
  IUPAC_TO_RNA_AGNOSTIC_SUBSET,
  IUPAC_TO_SUBSET,
  IupacPartialMapping,
  MELTING_TEMP_PREFERENCES_DEFAULTS,
  MeltingTempMethod,
  NUCLEOTIDE_TYPE_TO_COMPLEMENT_TABLE,
  NucleotideType,
  PREPARED_OD260_INDIVIDUAL_DATA,
  PREPARED_OD260_NN_DATA,
  RNA_IUPAC_TO_BASES,
  StandardBase,
  StandardDNABase,
  TM_BRESLAUER_DNA_NN_PARAMETERS,
  TM_SANTALUCIA_DNA_NN_PARAMETERS,
  TM_SANTALUCIA_DNA_NN_TERMINAL_BASES_TABLE,
  TM_XIA_RNA_NN_PARAMETERS,
  TM_XIA_RNA_NN_TERMINAL_BASES_TABLE,
  VALID_BASE_PATTERN,
  ResidueType,
} from './BioConstants';
import {
  ORFMatch,
  forwardStrandOrfsWithStartCodon,
  reverseStrandOrfsWithStartCodon,
} from './orfsWithStartCodon';
import {Sequence} from './Sequence';

const MAX_REGEX_LENGTH = 32768;

class LongQueryException extends ErrorSubclass {}

const AA_TO_DNA_CODON_REGEX: {[aa: string]: string} = {};
const AA_TO_DNA_REVCOMP_CODON_REGEX: {[aa: string]: string} = {};
const AA_TO_RNA_CODON_REGEX: {[aa: string]: string} = {};

for (const [aa, cs] of Object.entries(AMINO_ACID_TO_DEGENERATE_DNA_CODONS)) {
  AA_TO_DNA_CODON_REGEX[aa] = `(${cs.join('|')})`;
  AA_TO_DNA_REVCOMP_CODON_REGEX[aa] = `(${cs
    .map((codon: string) => revcomp(codon, DNA_COMPLEMENT_TABLE))
    .join('|')})`;
  AA_TO_DNA_CODON_REGEX[aa.toLowerCase()] = AA_TO_DNA_CODON_REGEX[aa];
  AA_TO_DNA_REVCOMP_CODON_REGEX[aa.toLowerCase()] = AA_TO_DNA_REVCOMP_CODON_REGEX[aa];
}

for (const [aa, rx] of Object.entries(AA_TO_DNA_CODON_REGEX)) {
  AA_TO_RNA_CODON_REGEX[aa] = rx.replace(/T/g, 'U');
}

function reverse(seq: string): string {
  let r = '';
  for (const base of Array.from(seq)) {
    r = base + r;
  }
  return r;
}

function complement(seq: string, complementTable: IupacPartialMapping): string {
  // NB client-side complement conservatively passes base through if its not in the lookup table.
  let comp = '';
  for (const base of Array.from(seq)) {
    comp += complementTable[base as IUPAC] || base;
  }
  return comp;
}

function revcomp(seq: string, complementTable: IupacPartialMapping): string {
  // Instead of calling reverse(complement(seq)), we do them both at the
  // same time to make this faster.
  let rc = '';
  let i = seq.length - 1;
  while (i >= 0) {
    const base = seq[i--];
    rc += complementTable[base as IUPAC] || base;
  }
  return rc;
}

// bigrams('abcde') === ['ab', 'bc', 'cd', 'de']
function bigrams(seq: string): Array<string> {
  const result = [];
  for (let i = 0; i <= seq.length - 2; i += 1) {
    result.push(seq.slice(i, i + 2));
  }
  return result;
}

interface GenerateRegexOptions {
  allowedChars?: Array<string>;
  maxMismatches?: number;
  rnaAgnostic?: boolean;
}
type ImitationRegExpArray = (Array<string> & {index: number}) | null;
export interface ImitationRegExp {
  lastIndex: number;
  exec: (search: string) => ImitationRegExpArray;
}
function generateRegex(seq: string, options: GenerateRegexOptions = {}): RegExp | ImitationRegExp {
  const fullOptions: Required<GenerateRegexOptions> = {
    allowedChars: [],
    maxMismatches: 0,
    rnaAgnostic: false,
    ...options,
  };

  if (fullOptions.maxMismatches === 0) {
    const regexMap = fullOptions.rnaAgnostic ? IUPAC_TO_RNA_AGNOSTIC_REGEX : IUPAC_TO_REGEX;
    let regexstr = '';
    for (const base of Array.from(seq)) {
      if (fullOptions.allowedChars.includes(base)) {
        regexstr += base;
      } else {
        const c = regexMap[base.toUpperCase() as IUPAC];
        if (c) {
          regexstr += c;
        } else {
          throw new Error(`Unknown base given (${base}).`);
        }
      }
    }

    if (regexstr.length >= MAX_REGEX_LENGTH) {
      throw new LongQueryException(`The query generated a RegExp that is too big: ${regexstr}`);
    }

    return RegExp(regexstr, 'gi');
  } else {
    const subsetMap = fullOptions.rnaAgnostic ? IUPAC_TO_RNA_AGNOSTIC_SUBSET : IUPAC_TO_SUBSET;
    // create pattern with an array of the possible characters for each position
    const pattern: Array<Array<string>> = [];
    for (const base of Array.from(seq)) {
      if (fullOptions.allowedChars.includes(base)) {
        pattern.push([base]);
      } else {
        const subs = subsetMap[base.toUpperCase() as IUPAC];
        if (subs) {
          pattern.push(subs);
        } else {
          throw new Error(`Unknown base given (${base}).`);
        }
      }
    }
    return {
      lastIndex: 0,
      exec(searchString: string): ImitationRegExpArray {
        // searchString should be same case as input pattern
        let match = null;
        // search manually. appears to be fast enough on seqs that aren't really big
        for (
          let i = this.lastIndex, end = searchString.length - pattern.length + 1, asc = this.lastIndex <= end;
          asc ? i < end : i > end;
          asc ? i++ : i--
        ) {
          let j;
          let asc1;
          let end1;
          let mismatches = 0;
          // eslint-disable-next-line benchlint/disallow-comma-operator
          for (j = 0, end1 = pattern.length, asc1 = 0 <= end1; asc1 ? j < end1 : j > end1; asc1 ? j++ : j--) {
            // using indexOf on a small list seems to be faster than property checking
            if (!pattern[j].includes(searchString[i + j])) {
              mismatches += 1;
              if (mismatches > fullOptions.maxMismatches) {
                break;
              }
            }
          }
          if (j === pattern.length) {
            match = [searchString.substr(i, pattern.length)] as Exclude<ImitationRegExpArray, null>;
            match.index = i;
            break;
          }
        }
        return match;
      },
    };
  }
}

function generateAARegexHelper(
  aminoAcids: string,
  table: {[aa: string]: string},
  options: {revcomp: boolean} = {revcomp: false}
): RegExp {
  let regexstr = '';
  for (const aa of Array.from(aminoAcids)) {
    const c = table[aa];
    if (c) {
      if (options.revcomp) {
        regexstr = c + regexstr;
      } else {
        regexstr += c;
      }
    } else {
      throw new Error(`Unknown amino acid given (${aa})`);
    }
  }
  if (regexstr.length >= MAX_REGEX_LENGTH) {
    throw new LongQueryException(`The query generated a RegExp that is too big: ${regexstr}`);
  }

  return RegExp(regexstr, 'gi');
}

function generateAARegexForDNA(aminoAcids: string, options: {revcomp: boolean} = {revcomp: false}): RegExp {
  const table = options.revcomp ? AA_TO_DNA_REVCOMP_CODON_REGEX : AA_TO_DNA_CODON_REGEX;
  return generateAARegexHelper(aminoAcids, table, options);
}

function generateAARegexForRNA(aminoAcids: string): RegExp {
  const table = AA_TO_RNA_CODON_REGEX;
  return generateAARegexHelper(aminoAcids, table, {revcomp: false});
}
function enzymeCutsWithinRestrictionSite(enzyme: Enzyme & {length: number}): boolean {
  const {cut, cut2, offset, offset2, length} = enzyme;

  if (cut < 0 || cut > length) {
    return false;
  }
  if (cut + offset < 0 || cut + offset > length) {
    return false;
  }
  if (cut2 == null) {
    return true;
  }
  if (cut2 < 0 || cut2 > length) {
    return false;
  }
  if (cut2 + offset2! < 0 || cut2 + offset2! > length) {
    return false;
  }
  return true;
}

function digestableEnzyme(enzyme: Enzyme): DigestableEnzyme {
  const enz = enzyme as DigestableEnzyme;

  let pad;
  const {cut} = enz;
  const {cut2} = enz;
  const {offset} = enz;
  let matchingCut = cut;
  let matchingCut2 = cut2;

  function ns(len: number): string {
    let n = '';
    for (let i = 0; i < len; i++) {
      n += 'N';
    }
    return n;
  }

  // Left and right are with respect to the left of the matchingRegion.
  if (offset > 0) {
    enz.right = cut + offset;
    enz.left = cut;
  } else {
    enz.right = cut;
    enz.left = cut + offset;
  }

  let matchingRegion = '';

  // If we start with N's
  if (enz.left < 0) {
    pad = -enz.left;
    matchingRegion = ns(pad);
    enz.left += pad;
    enz.right += pad;
    matchingCut += pad;
    if (matchingCut2 != null) {
      matchingCut2 += pad;
    }
  }
  matchingRegion += enz.seq;

  // If we end with N's
  if (enz.right > matchingRegion.length) {
    pad = enz.right - matchingRegion.length;
    matchingRegion += ns(pad);
  }

  enz.matchingRegion = matchingRegion;
  enz.matchingCut = matchingCut;
  enz.matchingCut2 = matchingCut2;
  return enz;
}

function calculateRevcompForRangeFeature(
  seqLength: number,
  start: number,
  end: number
): {start: number; end: number} {
  return {
    end: sequtil.mod(seqLength - start, seqLength),
    start: sequtil.mod(seqLength - end, seqLength),
  };
}

function revcompFeatures(seqLength: number, features: Array<PolymerFeatureAttributes>): Array<number> {
  const result = [];
  for (const feature of features) {
    const {start, end} = calculateRevcompForRangeFeature(seqLength, feature.start, feature.end);
    feature.start = start;
    feature.end = end;
    if (feature.bind_position != null) {
      feature.bind_position = seqLength - feature.bind_position - 1;
    }
    result.push((feature.strand = OPPOSITE_STRAND[feature.strand]));
  }
  return result;
}

/**
 * Equivalent of revcompFeatures but for the underlying attributes of a part.
 */
function revcompParts(seqLength: number, parts: Array<PartAttributes>): void {
  for (const part of parts) {
    const {start, end} = calculateRevcompForRangeFeature(seqLength, part.start, part.end);
    part.start = start;
    part.end = end;
    part.reverse = !part.reverse;
    part.complement = !part.complement;
  }
}

/**
 * Equivalent of cloneWrappableFeature but for the underlying attributes of a part.
 * Adjusts part start/end with reference to a subselection of a sequence.
 */
function clonePartAttributes(
  p: PartAttributes,
  selectionStart: number,
  selectionEnd: number,
  seqLength: number
): PartAttributes {
  assert(
    sequtil.isCircularRangeWithin(p.start, p.end, selectionStart, selectionEnd, seqLength),
    'Expected part to be fully contained within selection.'
  );
  // Calculate the offset to add to the part, which is the distance between the origin of this sequence and
  // the start of the selection.
  const offsetDistance =
    p.start < selectionStart
      ? sequtil.length(selectionStart, selectionEnd, seqLength) - selectionEnd
      : -selectionStart;
  const start = sequtil.mod(p.start + offsetDistance, seqLength);
  const end = sequtil.mod(p.end + offsetDistance, seqLength);
  return {...p, start, end};
}

function cloneWrappableFeature(
  feature: PolymerFeatureModel,
  start: number,
  end: number,
  length: number
): PolymerFeatureModel {
  // Calculate the offset to add to the feature, which is the distance between the origin of this sequence and
  // the start of the selection.
  let offsetDistance;
  if (feature.start() < start) {
    // edgecase on tail end of wrapping slice/selection
    offsetDistance = sequtil.length(start, end, length) - end;
  } else {
    offsetDistance = -start;
  }
  const newFeature = feature.clone(offsetDistance);
  newFeature.set({
    start: sequtil.mod(newFeature.start(), length),
    end: sequtil.mod(newFeature.end(), length),
  });
  if (newFeature.get('bind_position')) {
    newFeature.set('bind_position', sequtil.mod(newFeature.get('bind_position')!, length));
  }
  return newFeature;
}

function search(
  sequence: string,
  bases: string,
  circular: boolean,
  ds: boolean = true
): Array<{sequence: string; index: number; strand: Strand}> {
  let matches;
  let searchString;
  const fwdPattern = generateRegex(bases);
  const revPattern = generateRegex(revcomp(bases, DNA_COMPLEMENT_TABLE));

  if (circular) {
    searchString = sequence + sequence.slice(0, bases.length - 1);
  } else {
    searchString = sequence;
  }

  const totalMatches = [];
  // oxlint-disable-next-line no-cond-assign
  while ((matches = fwdPattern.exec(searchString))) {
    fwdPattern.lastIndex = matches.index + 1;
    totalMatches.push({
      sequence: matches[0],
      index: matches.index,
      strand: 1 as Strand,
    });
  }

  if (ds) {
    // oxlint-disable-next-line no-cond-assign
    while ((matches = revPattern.exec(searchString))) {
      revPattern.lastIndex = matches.index + 1;
      totalMatches.push({
        sequence: revcomp(matches[0], DNA_COMPLEMENT_TABLE),
        index: matches.index,
        strand: -1 as Strand,
      });
    }
  }
  return totalMatches;
}

function normalize(seq: string): string {
  // FIXME(saif): does seq ever have spaces anyways?
  // we should just return seq.toUpperCase()
  return seq.replace(/[ ]/g, '').toUpperCase();
}

function removeGaps(residues: string): string {
  return residues.replace(GAP_PATTERN, '');
}

function _translate(
  seq: string,
  nucleotideType: NucleotideType,
  codonTranslationTable: CodonTranslationTable,
  coerceStartCodonToMethionine: boolean,
  ignoreGaps: boolean = false
): string {
  let normalizedSequence = normalize(seq);
  if (ignoreGaps) {
    normalizedSequence = removeGaps(normalizedSequence);
  }
  const codons = Array.from(chunk(normalizedSequence, 3));
  if (normalizedSequence.length % 3 !== 0) {
    // Remove incomplete codon at end, if applicable.
    codons.pop();
  }
  const translationTable = (
    nucleotideType === NucleotideType.DNA
      ? geneticCodesDNA[codonTranslationTable].code
      : geneticCodesRNA[codonTranslationTable].code
  ) as GeneticCodeData['code'];

  const translatedCodons = codons.map((codon) => translationTable[codon] ?? '?');
  if (coerceStartCodonToMethionine && translatedCodons.length >= 1) {
    translatedCodons[0] = 'M';
  }
  return translatedCodons.join('');
}

const translate = lruMemoize(
  _translate,
  {max: 1000},
  (
    seq: string,
    nucleotideType: NucleotideType,
    codonTranslationTable: CodonTranslationTable,
    coerceStartCodonToMethionine: boolean,
    ignoreGaps: boolean = false
  ) => `${seq};${nucleotideType};${codonTranslationTable};${coerceStartCodonToMethionine};${ignoreGaps}`
);

function translateOnStrand(
  bases: string,
  strand: Strand,
  nucleotideType: NucleotideType,
  codonTranslationTable: CodonTranslationTable,
  coerceStartCodonToMethionine: boolean,
  ignoreGaps: boolean = false
): string {
  if (strand === -1) {
    bases = revcomp(bases, NUCLEOTIDE_TYPE_TO_COMPLEMENT_TABLE[nucleotideType]);
  }
  return translate(bases, nucleotideType, codonTranslationTable, coerceStartCodonToMethionine, ignoreGaps);
}

function sign(val: number): number {
  if (val > 0) {
    return 1;
  } else if (val < 0) {
    return -1;
  } else {
    return 0;
  }
}

function random(length: number, gcFraction: number = 0.5): string {
  const cutoff = Math.ceil(length * gcFraction);
  const bases = [];
  for (let i = 0; i < cutoff; i++) {
    bases.push(Math.random() > 0.5 ? 'C' : 'G');
  }
  for (let j = cutoff, end1 = length, asc1 = cutoff <= end1; asc1 ? j < end1 : j > end1; asc1 ? j++ : j--) {
    bases.push(Math.random() > 0.5 ? 'A' : 'T');
  }
  return _.shuffle(bases).join('');
}

function slice(bases: string, start: number, end: number, circular: boolean = false): string {
  if (start < end) {
    return bases.slice(start, end);
  }

  assert(circular || end === 0, 'Invalid start and end for linear sequence', {start, end});
  return bases.slice(start) + bases.slice(0, end);
}

export function circularArraySlice<T>(
  arr: Array<T>,
  start: number,
  end: number,
  circular: boolean = false
): Array<T> {
  if (start < end) {
    return arr.slice(start, end);
  }

  assert(circular || end === 0, 'Invalid start and end for linear sequence', {start, end});
  return arr.slice(start).concat(arr.slice(0, end));
}

interface MeltingTemperatureOptions {
  method: MeltingTempMethod; // the algorithm path to use
  //    primer3: oligotm derived SantaLucia 1998 thermodynamic parameters
  //    thermo: thermo webtools tmc derived modified Breslauer parameters
  //    xia: RNA option, uses Xia parameters for RNA duplexes
  monovalentConc: number | null; // (global) concentration of monovalent cation in mM
  nnMaxLen: number | null; // (primer3) threshold at which to switch to longSeqTm Heuristic in bp
  dnaConc: number | null; // (global) concentration of DNA/RNA strands in nM
  divalentConc: number | null; // (primer3) concentration of divalent cations in mM
  dntpConc: number | null; // (primer3) concentration of dNTP in mM
}

function validateMeltingTempMethod(
  seq: string,
  method: MeltingTempMethod,
  nucleotideType: NucleotideType
): void {
  assert(
    method !== MeltingTempMethod.primer3 || nucleotideType === NucleotideType.DNA,
    `nucleotideType not valid for Tm method primer3 (seq: ${seq})`
  );
  assert(
    method !== MeltingTempMethod.thermo || nucleotideType === NucleotideType.DNA,
    `nucleotideType not valid for Tm method thermo (seq: ${seq})`
  );
  assert(
    method !== MeltingTempMethod.xia || nucleotideType === NucleotideType.RNA,
    `nucleotideType not valid for Tm method xia (seq: ${seq})`
  );
}

function meltingTemperature(seq: string, nucleotideType: NucleotideType): number {
  /*
  General interface into a number of melting temperature calculation methods
  */

  const opts = _.clone(
    nucleotideType === NucleotideType.DNA
      ? preferences.get(PrimerPreferences.MELTING_TEMP_DNA)
      : preferences.get(PrimerPreferences.MELTING_TEMP_RNA)
  );

  // handle nulls...
  for (const [key, value] of Object.entries(opts)) {
    // eslint-disable-next-line benchlint/no-eqeqeq-null
    if (value === null) {
      delete opts[key as keyof MeltingTemperatureOptions];
    }
  }

  const fullOpts: MeltingTemperatureOptions = {
    ...MELTING_TEMP_PREFERENCES_DEFAULTS,
    nnMaxLen: 80,
    ...opts,
  };

  seq = normalize(seq);
  validateMeltingTempMethod(seq, fullOpts.method, nucleotideType);

  if (seq.length === 0) {
    return NaN;
  }

  if (fullOpts.method === MeltingTempMethod.primer3) {
    // FIXME: Treats all ambiguous bases as N, should use better numbers.
    seq = seq.replace(/[^AGTC]/g, 'N');
    if (seq.length > fullOpts.nnMaxLen!) {
      return longSeqTm(seq, fullOpts.monovalentConc!, fullOpts.divalentConc!, fullOpts.dntpConc!);
    } else {
      return oligoTm(
        seq,
        fullOpts.dnaConc!,
        fullOpts.monovalentConc!,
        fullOpts.divalentConc!,
        fullOpts.dntpConc!
      );
    }
  } else if (fullOpts.method === MeltingTempMethod.thermo) {
    return breslauerTm(seq, fullOpts.dnaConc!, fullOpts.monovalentConc!);
  } else if (fullOpts.method === MeltingTempMethod.xia) {
    return xiaTm(seq.replace(/[^AGUC]/g, 'N'), fullOpts.dnaConc!, fullOpts.monovalentConc!);
  } else {
    throw new Error('Invalid melting temperature method selected.');
  }
}

const TKELVIN = 273.15;
const R = 1.987; // gas constant in cal/(K * mol)

function breslauerTm(
  seq: string,
  oligoConc: number, // in nM
  monovalentConc: number // in mM
): number {
  // re-normalize nice units into molar concentrations
  const cOligo = oligoConc * 1e-9;
  const cSalt = monovalentConc * 1e-3;

  // mystery number, this might just be incorrect since most of the literature says it should be 1 for symmetric
  // sequences and 4 otherwise. Might be originally from the Thermo Fisher Tm Calculator.
  // Keeping the same for consistency.
  const b = 160;

  const meltingTemp = nearestNeighborOligoTm(
    seq,
    NucleotideType.DNA,
    cOligo,
    b,
    TM_BRESLAUER_DNA_NN_PARAMETERS,
    {
      dH: -3400, // cal/mol from Sugimoto, 1996
      dS: -12.4, // mystery number, but seems to be seems to be in the ballpark at least
    },
    {dH: 0, dS: 0} // no corrections given for symmetry or terminal bases
  );
  return clamp(0, 95, meltingTemp + schildkrautSaltCorrection(cSalt));
}

function xiaTm(
  seq: string,
  oligoConc: number, // in nM
  monovalentConc: number // in mM
): number {
  // re-normalize nice units into molar concentrations
  const cOligo = oligoConc * 1e-9;
  const cSalt = monovalentConc * 1e-3;

  const b = isSymmetric(seq, NucleotideType.RNA) ? 1 : 4;

  const meltingTemp = nearestNeighborOligoTm(
    seq,
    NucleotideType.RNA,
    cOligo,
    b,
    TM_XIA_RNA_NN_PARAMETERS,
    {
      dH: 3610,
      dS: -1.5,
    },
    {dH: 0, dS: -1.4},
    TM_XIA_RNA_NN_TERMINAL_BASES_TABLE
  );
  return meltingTemp + schildkrautSaltCorrection(cSalt);
}

/*
 * The equations for calculating melting temperature of oligos loses accuracy as the number of bases grows.
 * This formula should be more accurate for longer DNA polymers. Formula is from primer3 based on Bolton and McCarthy (1962)
 * with the simple salt correction from Schildkraut and Lifson (1965).
 */
function longSeqTm(seq: string, saltConc: number, divalentConc: number, dntpConc: number): number {
  let gcCount = 0;
  let nCount = 0;
  for (const base of Array.from(seq)) {
    switch (base) {
      case 'C':
      case 'G':
        gcCount++;
        break;
      case 'N':
        nCount++;
        break;
      default:
        break;
    }
  }

  gcCount += nCount / 2;
  saltConc += divalentToMonovalent(divalentConc, dntpConc);

  return 81.5 + schildkrautSaltCorrection(saltConc / 1000) + (41 * gcCount - 600) / seq.length;
}

/*
 * Calculate the melting temperature of a DNA oligo using SantaLucia parameters and salt concentration correction.
 */
function oligoTm(
  seq: string,
  dnaConc: number,
  saltConc: number,
  divalentConc: number,
  dntpConc: number
): number {
  const b = isSymmetric(seq, NucleotideType.DNA) ? 1 : 4;
  // get modified salt concentration in mM by taking into account divalent ions, normalize to molar concentration
  const modifiedSalt = (saltConc + divalentToMonovalent(divalentConc, dntpConc)) * 1e-3;

  return nearestNeighborOligoTm(
    seq,
    NucleotideType.DNA,
    dnaConc * 1e-9,
    b,
    TM_SANTALUCIA_DNA_NN_PARAMETERS,
    {dH: 0, dS: santaLuciaSaltCorrection(modifiedSalt, seq)},
    {dH: 0, dS: -1.4},
    TM_SANTALUCIA_DNA_NN_TERMINAL_BASES_TABLE
  );
}

/*
 * Calculate the estimated melting temperature in degrees Celsius for an oligo based on the Nearest Neighbor
 * model using given parameters. The formula does not account for salt concentration so a correction for salt
 * should be added to the result if the units are in degrees Celsius or included in basicInitiationParameters
 * if the units are in terms of change in entropy.
 */
function nearestNeighborOligoTm<BaseSet extends IUPAC>(
  seq: string, // assume that the seqeuence charset is typed as BaseSet, a subset of IUPAC
  nucleotideType: NucleotideType,
  cOligo: number, // oligo strand concentration in molar concentration
  b: number, // divisor value for oligo concentration
  nnParameters: {[base in BaseSet]: {[base in BaseSet]: EnergyDeltas}},
  basicInitiationParameters: EnergyDeltas,
  symmetryParameters: EnergyDeltas, // energy deltas to apply to self-complementary sequences
  terminalBaseParameters?: {[base in BaseSet]: EnergyDeltas} // parameters for start/end bases
): number {
  const extraParams = [basicInitiationParameters];
  if (isSymmetric(seq, nucleotideType)) {
    // account for energy deltas associated with symmetric sequences
    extraParams.push(symmetryParameters);
  }
  if (terminalBaseParameters) {
    // account for energy deltas associated with terminal bases
    const firstBase = seq[0] as BaseSet;
    const lastBase = seq[seq.length - 1] as BaseSet;
    extraParams.push(terminalBaseParameters[firstBase]);
    extraParams.push(terminalBaseParameters[lastBase]);
  }

  const {dH, dS} = bigrams(seq)
    .map(([baseA, baseB]) => nnParameters[baseA as BaseSet][baseB as BaseSet])
    .concat(extraParams)
    .reduce((a, b) => ({dH: a.dH + b.dH, dS: a.dS + b.dS}));

  return dH / (R * Math.log(cOligo / b) + dS) - TKELVIN;
}

/*
 * Calculate polymer melting temperature correction from salt concentration based on formula from
 * Schildkraut & Lifson (1965). Salt concentration should be given in molar concentration and the return value is
 * change in degrees Celsius.
 */
function schildkrautSaltCorrection(cSalt: number): number {
  return 16.6 * Math.log10(cSalt);
}

/*
 * Calculate oligo melting temperature correction from salt concentration and sequence length
 * based on formula from SantaLucia (1998). Salt concentration should be given in molar concentration, return value is
 * change in entropy.
 */
function santaLuciaSaltCorrection(cSalt: number, seq: string): number {
  const N = seq.length - 1; // total phosphates in the duplex divided by 2
  return 0.368 * N * Math.log(cSalt);
}

function isSymmetric(seq: string, nucleotideType: NucleotideType): boolean {
  if (seq.length % 2 === 1) {
    return false;
  }
  // Check whether the sequence is self-complementary
  return seq === revcomp(seq, NUCLEOTIDE_TYPE_TO_COMPLEMENT_TABLE[nucleotideType]);
}

/*
 * Convert divalent salt concentration to monovalent, based on primer3. Formula seems to be taken from
 * von Ahsen (2001). Concentrations should be given in millimolar and return value is also in millimolar.
 */
function divalentToMonovalent(divalent: number, dntp: number): number {
  if (divalent === 0) {
    dntp = 0;
  }
  if (divalent < dntp) {
    divalent = dntp;
  }
  return 120 * Math.sqrt(divalent - dntp);
}

function _dotproduct(objA: {[k: string]: number}, objB: {[k: string]: number}): number {
  let sum = 0;
  for (const [key, objBVal] of Object.entries(objB)) {
    // eslint-disable-next-line benchlint/no-in-operator
    if (key in objA) {
      sum += objA[key] * objBVal;
    }
  }
  return sum;
}

function gc(seq: string, nucleotideType: NucleotideType): number {
  assert(Boolean(nucleotideType), 'Expected a nucleotideType!');
  return (
    _dotproduct(count(seq, nucleotideType), {
      A: 0,
      U: 0,
      T: 0,
      G: 1,
      C: 1,
    }) / seq.length
  );
}

function mw(seq: string): number {
  // returns molecular weight in g/mol of DNA sequence
  return (
    _dotproduct(count(seq, NucleotideType.DNA), {
      A: 313.21,
      U: 0,
      T: 304.2,
      C: 289.18,
      G: 329.21,
      I: 314,
    }) - 61.96
  );
}

function e260(seq: string): number {
  // returns extinction coefficient in l/(mol x cm)
  const vals = bigrams(seq).map(
    (pair) => PREPARED_OD260_NN_DATA[pair[0] as StandardDNABase][pair[1] as StandardDNABase]
  );
  const nn = benchlingUtil.sum(vals);

  const single = _dotproduct(
    _.countBy(seq.slice(1, -1), (b) => b.toUpperCase()),
    PREPARED_OD260_INDIVIDUAL_DATA
  );

  return nn - single;
}

/**
 * Return object mapping bases to the number of times that base appears, accounting for degenerates
 */
function count(seq: string, nucleotideType: NucleotideType): {[b in StandardBase]: number} {
  assert(Boolean(nucleotideType), 'Expected a nucleotideType!');
  const rv = {
    A: 0,
    T: 0,
    G: 0,
    C: 0,
    U: 0,
  };
  const baseTable = nucleotideType === NucleotideType.DNA ? DNA_IUPAC_TO_BASES : RNA_IUPAC_TO_BASES;
  type BaseSet = keyof typeof baseTable;
  for (const base of Array.from(seq)) {
    const normBase = base.toUpperCase();
    let expBases = baseTable[normBase as BaseSet];
    assert(expBases != null, `${normBase} not in baseTable (seq: ${seq})`);
    expBases = expBases || '';
    for (const b of Array.from(expBases)) {
      rv[b as StandardDNABase] += 1 / expBases.length;
    }
  }
  return rv;
}

function isValidBase(base: string, complementTable: IupacPartialMapping): boolean {
  // eslint-disable-next-line benchlint/no-in-operator
  return base in complementTable;
}

function sanitize(seq: string, nucleotideType: NucleotideType, skipUT: boolean = false): string {
  return getSanitizedBasesAndSanitizationErrors(seq, nucleotideType, skipUT).sanitizedBases;
}

interface SanitizedBasesAndErrors {
  sanitizedBases: string;
  errors: {
    convertedUToT: boolean;
    convertedTToU: boolean;
    convertedIToN: boolean;
    removedInvalidBases: boolean;
  };
}

function getSanitizedBasesAndSanitizationErrors(
  seq: string,
  nucleotideType: NucleotideType,
  skipUT: boolean = false
): SanitizedBasesAndErrors {
  assert(Boolean(nucleotideType), 'Expected a nucleotideType!');
  const sanitizedBasesAndErrors = {
    sanitizedBases: '',
    errors: {
      convertedUToT: false,
      convertedTToU: false,
      convertedIToN: false,
      removedInvalidBases: false,
    },
  };

  const result = [];
  const commonBases = ['a', 'A', 'g', 'G', 'c', 'C', ...DEGENERATE_BASES];

  for (const base of Array.from(seq)) {
    // This should stay in sync with pybio/util.py::sanitize
    if (commonBases.includes(base)) {
      result.push(base);
    } else if (base === 'I' || base === 'i') {
      // Convert dI (the "universal" base) to N
      sanitizedBasesAndErrors.errors.convertedIToN = true;
      result.push(matchCase(base, 'N'));
    } else if (base === 'U' || base === 'u') {
      if (nucleotideType === NucleotideType.DNA && !skipUT) {
        sanitizedBasesAndErrors.errors.convertedUToT = true;
        result.push(matchCase(base, 'T'));
      } else {
        result.push(base);
      }
    } else if (base === 'T' || base === 't') {
      if (nucleotideType === NucleotideType.RNA && !skipUT) {
        sanitizedBasesAndErrors.errors.convertedTToU = true;
        result.push(matchCase(base, 'U'));
      } else {
        result.push(base);
      }
    } else {
      sanitizedBasesAndErrors.errors.removedInvalidBases = true;
    }
  }

  sanitizedBasesAndErrors.sanitizedBases = result.join('');
  return sanitizedBasesAndErrors;
}

function reverseTranscribeRNA(seq: string): string {
  return rnaToDNA(seq);
}

function didConvertIToN(originalBases: string, actualBases: string): boolean {
  return originalBases !== actualBases && originalBases.replace(/I/g, 'N').replace(/i/g, 'n') === actualBases;
}

function didConvertUToT(originalBases: string, actualBases: string): boolean {
  return originalBases !== actualBases && originalBases.replace(/U/g, 'T').replace(/u/g, 't') === actualBases;
}

// Note that this does not support non-standard amino acids. To include those, use areAminoAcidsValid.
function isValidAminoAcids(seq: string): boolean {
  // eslint-disable-next-line benchlint/no-in-operator, no-restricted-syntax
  return _.every(seq, (aa) => aa in AA_TO_DNA_CODON_REGEX);
}

export function sanitizeAminoAcids(residues: string): string {
  return residues
    .split('')
    .filter((residue) => areAminoAcidsValid(residue))
    .join('');
}

function isValidSequence(seq: string, nucleotideType: NucleotideType): boolean {
  assert(Boolean(nucleotideType), 'Expected a nucleotideType!');
  // eslint-disable-next-line no-restricted-syntax
  return _.every(seq, (base) => isValidBase(base, NUCLEOTIDE_TYPE_TO_COMPLEMENT_TABLE[nucleotideType]));
}

function isValidMixedBaseSequence(seq: string): boolean {
  return VALID_BASE_PATTERN.test(seq);
}

function isSimpleDNASequence(seq: string): boolean {
  // eslint-disable-next-line no-restricted-syntax
  return _.every(seq, (base) => 'ATGC'.includes(base.toUpperCase()));
}

function isSimpleRNASequence(seq: string): boolean {
  // eslint-disable-next-line no-restricted-syntax
  return _.every(seq, (base) => 'AUGC'.includes(base.toUpperCase()));
}

function isSimpleSequence(seq: string): boolean {
  // eslint-disable-next-line no-restricted-syntax
  return _.every(seq, (base) => 'ATUGC'.includes(base.toUpperCase()));
}

function formatBaseCount(baseCount: number): string {
  // Keep in sync with server-side format_base_count.

  const table = [
    [1, 'bp'],
    [1000, 'kb'],
    [1000000, 'Mb'],
    [1000000000, 'Gb'],
  ];
  const numDecimalDigits = 1;

  if (baseCount >= 0) {
    for (let i = 0; i < table.length; i++) {
      const entry = table[i];
      const [cutoff, ext] = entry;
      const num = (baseCount / (cutoff as number)).toFixed(numDecimalDigits);
      // Maximum length of num should be 4 + numDecimalDigits (unless we have
      // no larger units). For example "999.9" has length 5 but "1000.0" has
      // length 6.
      if (num.length <= 4 + numDecimalDigits || i === table.length - 1) {
        // If i = 0, then ext = 'bp' and decimal digits are not needed.
        return `${i === 0 ? baseCount : num} ${ext}`;
      }
    }
  }

  throw new Error(`Unable to format base count: ${baseCount}`);
}

export function formatAminoAcidCount(residueCount: number): string {
  return `${residueCount} ${benchlingUtil.pluralize(residueCount, 'residue')}`;
}

export function formatPolymerResidueCount(residueCount: number, residueType: ResidueType): string {
  if (residueType === ResidueType.AA) {
    return formatAminoAcidCount(residueCount);
  } else {
    return formatBaseCount(residueCount);
  }
}

export function formatPolymerResidueSubcount(
  residueCount: number,
  totalResidues: number,
  residueType: ResidueType
): string {
  if (residueType === ResidueType.AA) {
    return `${residueCount} of ${formatAminoAcidCount(totalResidues)}`;
  } else {
    return `${formatBaseCount(residueCount)} of ${formatBaseCount(totalResidues)}`;
  }
}

function isOrf(bases: string, startCodon: string = DEFAULT_DNA_START_CODON): boolean {
  bases = sanitize(bases, NucleotideType.DNA);
  if (bases.length < 6 || bases.length % 3 !== 0) {
    return false;
  }
  const endBases = bases.slice(bases.length - 3);
  return (
    bases.slice(0, 3).toUpperCase() === startCodon && DEGENERATE_DNA_CODE[endBases.toUpperCase()] === '*'
  );
}

function orfs(
  sequence: string,
  circular: boolean,
  nucleotideType: NucleotideType,
  isSingleStranded: boolean,
  startCodon: StandardDNACodon
): Array<ORFMatch> {
  const isRNA = nucleotideType === NucleotideType.RNA;
  // sanitize so that RNA Sequences are compatible with the DNA form of start codons
  if (isRNA) {
    sequence = sanitize(sequence, NucleotideType.DNA);
  }
  // TODO: The RNA logic gating could be removed if codons were RNA aware
  let results: Array<ORFMatch> = forwardStrandOrfsWithStartCodon(sequence, circular, startCodon);
  if (!isSingleStranded) {
    results = results.concat(reverseStrandOrfsWithStartCodon(sequence, circular, startCodon));
  }
  return results;
}

function digest(
  fragments: Array<Sequence>,
  enzymes: Array<DigestableEnzyme>,
  filterBand?: (frag: unknown) => boolean
): Array<Sequence> {
  let out = fragments;

  _(enzymes).each((e) => {
    const working: Array<Sequence> = [];
    _(out).each((seq) => {
      let frags = seq.digest(e).models;
      if (filterBand != null) {
        frags = _(frags).filter((f) => filterBand(f));
      }
      return working.push(...frags);
    });
    out = working;
  });

  return out;
}

function matchWithDegeneracy(sample?: string | null, reference?: IUPAC | null): boolean {
  if (sample == null || reference == null) {
    return false;
  }
  return sample.toUpperCase().match(`^${IUPAC_TO_REGEX[reference]}$`) != null;
}

function stickyCompatible(upstream: Sequence, downstream: Sequence): boolean {
  if (upstream != null && downstream != null) {
    if (upstream.get('circular') || downstream.get('circular')) {
      return false;
    }

    const up3 = normalize(upstream.overhang3());
    const dn5 = normalize(downstream.overhang5());

    const isDir = sign(upstream.get('offset3')) === sign(downstream.get('offset5'));
    const isCompl = up3 === dn5;

    return isDir && isCompl;
  } else {
    return true;
  }
}

/**
 * Return length of the resulting sequence you would get if you ran PCR with the given primers.
 */
function productLength(
  [forwardStart, forwardEnd]: [number, number],
  [reverseStart, reverseEnd]: [number, number],
  circular: boolean,
  length: number
): number {
  if (!circular) {
    if (reverseEnd === 0) {
      reverseEnd = length;
    }
    // If a primer's end cuts into the other primer's site
    if (forwardStart <= reverseStart && reverseEnd >= forwardEnd) {
      return reverseEnd - forwardStart;
    }
    return 0;
  }
  // Shift so forwardStart is at 0.
  const shift = forwardStart;
  forwardStart = 0;
  forwardEnd = sequtil.mod(forwardEnd - shift, length);
  reverseStart = sequtil.mod(reverseStart - shift, length);
  reverseEnd = sequtil.mod(reverseEnd - shift, length);
  if (reverseEnd === 0) {
    reverseEnd += length;
  }
  if (reverseEnd >= forwardEnd) {
    if (reverseStart >= reverseEnd) {
      // Reverse primer wraps, product is full circle and more (forwardStart -> reverseEnd)
      // For now we say there is no product.
      return 0;
    }
    // Product will go from forwardStart to reverseEnd
    return reverseEnd - forwardStart;
  }
  // Product will be entire sequence PLUS extra overlap between forwardEnd and reverseEnd
  // For now we say there is no product.
  return 0;
}

function hammingDistance(xstr: string, ystr: string): number {
  let d = 0;
  for (const [x, y] of _.zip(xstr, ystr)) {
    if (x !== y) {
      d++;
    }
  }
  return d;
}

function isDegenerate(seq: string): boolean {
  const bases = normalize(seq);
  return Array.from(bases).some((base) => DEGENERATE_BASES.includes(base));
}

function stripDegenerate(seq: string, nucleotideType: NucleotideType): string {
  return Array.from(sanitize(seq, nucleotideType))
    .filter((base) => !isDegenerate(base))
    .join('');
}

export function containsNonStandardAminoAcids(aminoAcids: string): boolean {
  return Array.from(normalize(aminoAcids)).some((aa) => NON_STANDARD_AMINO_ACIDS.includes(aa));
}

function nextNonGapIndex(bases: string, index: number): number {
  for (let i = index; i < bases.length; i++) {
    if (bases[i] !== GAP) {
      return i;
    }
  }
  return -1;
}

export function dnaToRNA(bases: string): string {
  return bases.replace(/T/g, 'U').replace(/t/g, 'u');
}

export function rnaToDNA(bases: string): string {
  return bases.replace(/U/g, 'T').replace(/u/g, 't');
}

export {
  count,
  digest,
  generateRegex,
  generateAARegexForDNA,
  generateAARegexForRNA,
  enzymeCutsWithinRestrictionSite,
  digestableEnzyme,
  reverse,
  complement,
  revcomp,
  calculateRevcompForRangeFeature,
  revcompFeatures,
  revcompParts,
  clonePartAttributes,
  cloneWrappableFeature,
  translateOnStrand,
  normalize,
  sign,
  random,
  meltingTemperature,
  NucleotideType,
  isSymmetric,
  isDegenerate,
  gc,
  orfs,
  isOrf,
  mw,
  e260,
  isValidAminoAcids,
  isValidBase,
  isValidSequence,
  isValidMixedBaseSequence,
  isSimpleDNASequence,
  isSimpleRNASequence,
  isSimpleSequence,
  sanitize,
  getSanitizedBasesAndSanitizationErrors,
  reverseTranscribeRNA,
  didConvertIToN,
  didConvertUToT,
  formatBaseCount,
  removeGaps,
  slice,
  search,
  matchWithDegeneracy,
  stickyCompatible,
  productLength,
  hammingDistance,
  LongQueryException,
  nextNonGapIndex,
  stripDegenerate,
};
